import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, QrCode, Wallet, CreditCard, Sparkles, KeyRound } from "lucide-react";

const GoogleIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.24 1.05-3.71 1.05-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.84 14.09A6.6 6.6 0 0 1 5.48 12c0-.72.13-1.42.36-2.09V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84Z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
  </svg>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
const startGoogleSignin = () => {
  const redirectUrl = window.location.origin + "/store";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("signin"); // "signin" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome ${u.name}`);
      if (u.must_change_password) nav("/change-password");
      else nav(u.role === "super_admin" ? "/admin" : "/store");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
      toast.success("If an account exists, we've sent a reset link.");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen paper-bg grid md:grid-cols-2">
      {/* Left panel */}
      <div className="hidden md:flex flex-col justify-between p-10 lg:p-12 bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 text-white relative overflow-hidden">
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -left-20 bottom-0 w-80 h-80 rounded-full bg-amber-300/20 blur-3xl" />
        <div className="relative flex items-center gap-2">
          <img src="/logo.png" alt="Print Plus" className="w-10 h-10 object-contain rounded-lg bg-white/15 backdrop-blur p-0.5" />
          <div className="leading-tight">
            <span className="font-display text-xl font-bold block">Print Plus</span>
            <span className="text-[10px] uppercase tracking-widest text-white/80 font-semibold">by AI Upaya</span>
          </div>
        </div>
        <div className="relative">
          <h1 className="text-4xl lg:text-5xl font-display font-black tracking-tighter leading-[1.05] mb-5">The operating system for modern print shops.</h1>
          <p className="text-white/85 text-lg mb-8 max-w-md">Manage stores, printers, orders, and revenue from a single console.</p>
          <div className="mt-1 mb-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-100 bg-amber-500/25 backdrop-blur border border-amber-300/40 rounded-full px-2.5 py-0.5">
            <Wallet className="w-3 h-3" />
            <span>Now with Pay-at-counter (cash) support</span>
          </div>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <div className="rounded-xl bg-white/10 backdrop-blur border border-white/15 p-3"><Wallet className="w-4 h-4 mb-1.5" /><div className="text-xs font-semibold">Pay at counter</div><div className="text-[10px] text-white/70">Cash-first by default</div></div>
            <div className="rounded-xl bg-white/10 backdrop-blur border border-white/15 p-3"><CreditCard className="w-4 h-4 mb-1.5" /><div className="text-xs font-semibold">Razorpay online</div><div className="text-[10px] text-white/70">100% to shop's account</div></div>
            <div className="rounded-xl bg-white/10 backdrop-blur border border-white/15 p-3"><ShieldCheck className="w-4 h-4 mb-1.5" /><div className="text-xs font-semibold">Secure</div><div className="text-[10px] text-white/70">JWT & tenant-scoped</div></div>
            <div className="rounded-xl bg-white/10 backdrop-blur border border-white/15 p-3"><QrCode className="w-4 h-4 mb-1.5" /><div className="text-xs font-semibold">QR-first</div><div className="text-[10px] text-white/70">Scan & print</div></div>
          </div>
        </div>
        <div className="relative text-sm text-white/70">© 2026 Print Plus by AI Upaya</div>
      </div>

      {/* Right panel */}
      <div className="flex flex-col md:justify-center px-5 py-8 md:p-6 relative">
        <div className="md:hidden flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Print Plus" className="w-8 h-8 object-contain" />
            <span className="font-display font-bold">Print Plus <span className="text-[9px] uppercase tracking-widest text-orange-500 font-semibold">· by AI Upaya</span></span>
          </Link>
          <Link to="/" className="text-xs text-slate-500 hover:text-orange-600 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" />Home</Link>
        </div>

        <div className="w-full max-w-sm mx-auto space-y-4">
          {mode === "signin" && (
            <form onSubmit={submit} className="space-y-5 bg-white p-7 md:p-8 rounded-2xl border border-slate-200 shadow-sm md:shadow-lg" data-testid="signin-form">
              <div>
                <h2 className="font-display text-2xl font-bold">Welcome back</h2>
                <p className="text-sm text-slate-500 mt-1">Sign in to your Print Plus dashboard</p>
              </div>

              {/* Google button */}
              <Button
                type="button"
                onClick={startGoogleSignin}
                variant="outline"
                className="w-full h-11 border-slate-200 hover:bg-slate-50 font-semibold"
                data-testid="google-signin-btn"
              >
                <GoogleIcon className="w-4 h-4 mr-2" />
                Sign in with Google
              </Button>

              <div className="relative flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">or with email</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" placeholder="you@example.com" className="h-11" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <button
                    type="button"
                    onClick={() => { setMode("forgot"); setForgotEmail(email); setForgotSent(false); }}
                    className="text-[11px] font-semibold text-orange-600 hover:text-orange-700"
                    data-testid="forgot-link"
                  >Forgot password?</button>
                </div>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="h-11" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 h-11 font-semibold" data-testid="login-submit">
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={submitForgot} className="space-y-5 bg-white p-7 md:p-8 rounded-2xl border border-slate-200 shadow-sm md:shadow-lg" data-testid="forgot-form">
              <div>
                <div className="w-10 h-10 rounded-xl bg-orange-100 grid place-items-center mb-3"><KeyRound className="w-5 h-5 text-orange-600" /></div>
                <h2 className="font-display text-2xl font-bold">Reset your password</h2>
                <p className="text-sm text-slate-500 mt-1">Enter the email you signed up with. We'll send you a reset link.</p>
              </div>
              {forgotSent ? (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="forgot-success">
                  If an account exists for <b>{forgotEmail}</b>, a reset link has been sent. Check your inbox (and spam).
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="femail">Email</Label>
                    <Input id="femail" type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} data-testid="forgot-email" placeholder="you@example.com" className="h-11" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 h-11 font-semibold" data-testid="forgot-submit">
                    {loading ? "Sending…" : "Send reset link"}
                  </Button>
                </>
              )}
              <button type="button" onClick={() => setMode("signin")} className="w-full text-xs text-slate-500 hover:text-slate-800 inline-flex items-center justify-center gap-1" data-testid="back-signin">
                <ArrowLeft className="w-3 h-3" />Back to sign in
              </button>
            </form>
          )}

          {/* New user callout */}
          <div className="rounded-2xl border border-orange-200 bg-gradient-to-br from-amber-50 via-orange-50 to-white p-4 flex items-center gap-3" data-testid="new-user-callout">
            <div className="w-10 h-10 rounded-xl bg-orange-500 grid place-items-center flex-shrink-0 shadow-md shadow-orange-500/20"><Sparkles className="w-5 h-5 text-white" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-display font-bold text-slate-900">New here?</div>
              <div className="text-[11px] text-slate-500">10 prints/day, free forever · Google sign-up in 20s</div>
            </div>
            <Link to="/get-started">
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600 h-9 text-xs font-semibold" data-testid="try-free-btn">Try free</Button>
            </Link>
          </div>

          <div className="hidden md:block text-xs text-slate-500 text-center">
            <Link to="/" className="hover:underline">← Back to home</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
